import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { PartyPicker } from '@/components/parties/PartyPicker';
import { useCreateCustomer } from '@/routes/customers/useCustomerActions';
import { useLeadSources } from '@/routes/leadSources/useLeadSources';
import type { PartyRef } from '@/routes/parties/useParties';

/**
 * "Add customer" — the create half of M1-30. Reached from the customers list,
 * which before this story had no way to add anyone: M1-01 shipped the
 * endpoint API-only, and M1-04 built the list and detail screens on top of it
 * without a create path.
 *
 * The fields are exactly `CreateCustomerRequest` and no more — `type` decides
 * whether the underlying Party is created as a Person or a LegalEntity
 * (M1-01), which is why it is the one field this dialog can set that
 * `EditCustomerDialog` cannot: changing it after creation would mean
 * recreating that Party as the other subtype.
 */
export function AddCustomerDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  /**
   * Announced by the page, which owns the live region and therefore owns the
   * `t()` call too — this reports the raw facts (the name, and whether M1-01's
   * duplicate-name flag came back set) rather than a pre-formatted string.
   * Passing a fully-built message here previously meant the page's own
   * `t('...added', { name })` wrapped it a SECOND time, producing "Added
   * Added Rosa Delgado — another customer already has this name..": doubled
   * word, doubled period. Found in the browser, not in a unit test — the
   * mocked API in every test happened to return `duplicateNameWarning: false`
   * except the one test written specifically for this case, and even that one
   * only asserted the message CONTAINED the right substring, not that it was
   * assembled exactly once.
   */
  onAdded: (name: string, duplicateNameWarning: boolean) => void;
}) {
  const { t } = useTranslation();
  const createCustomer = useCreateCustomer();

  const [type, setType] = useState<'Residential' | 'Commercial'>('Residential');
  const [name, setName] = useState('');
  // M1-25: the payer can be a brand-new Person/LegalEntity (today's flow,
  // unchanged) or one that already exists — created earlier from a
  // property's Parties section, say. Re-picking a fresh party clears any
  // previous pick rather than merging with it.
  const [payerMode, setPayerMode] = useState<'new' | 'existing'>('new');
  const [existingParty, setExistingParty] = useState<PartyRef | null>(null);
  const [primaryContact, setPrimaryContact] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  // M1-28: "how did you hear about us", PROMPTED and not required — which is
  // why neither of these appears in `canSubmit` below. An office booking an
  // emergency call at 2am must be able to save without answering it.
  const [leadSourceId, setLeadSourceId] = useState('');
  const [leadSourceDetail, setLeadSourceDetail] = useState('');
  // Active entries only (the API's default): a retired source is one the shop
  // has stopped offering, and the API refuses it on write anyway.
  const leadSources = useLeadSources();

  const canSubmit =
    (payerMode === 'existing' ? existingParty !== null : name.trim() !== '') &&
    line1.trim() !== '' &&
    city.trim() !== '' &&
    state.trim() !== '' &&
    postalCode.trim() !== '';

  function submit() {
    createCustomer.mutate(
      {
        type,
        name: payerMode === 'existing' ? null : name.trim(),
        existingPartyId: payerMode === 'existing' ? (existingParty?.partyId ?? null) : null,
        primaryContact: primaryContact.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        billingAddress: {
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          postalCode: postalCode.trim(),
        },
        notes: notes.trim() || null,
        leadSourceId: leadSourceId || null,
        // Detail without a source is refused by the API and by a check
        // constraint, so it is dropped here rather than sent to fail: a person
        // who typed a note and then cleared the picker meant to clear both.
        leadSourceDetail: leadSourceId ? leadSourceDetail.trim() || null : null,
      },
      {
        onSuccess: (created) => {
          // M1-01 made a duplicate name a warning, not a block: the customer
          // above is already created either way, so this only changes what
          // gets announced, never whether the dialog closes.
          onAdded(created.customer.name, created.duplicateNameWarning);
          onClose();
        },
      },
    );
  }

  const field =
    'h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

  return (
    <Dialog
      titleId="add-customer-title"
      title={t('customers.addCustomer.title')}
      onClose={onClose}
      testId="add-customer-dialog"
      dismissLabel={t('customers.addCustomer.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || createCustomer.isPending}
            onClick={submit}
          >
            {createCustomer.isPending
              ? t('customers.addCustomer.saving')
              : t('customers.addCustomer.confirm')}
          </Button>
        </>
      }
    >
      <fieldset className="text-sm">
        <legend className="mb-1 font-medium">{t('customers.addCustomer.payer')}</legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="payer-mode"
              checked={payerMode === 'new'}
              onChange={() => setPayerMode('new')}
            />
            {t('customers.addCustomer.payerNew')}
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="payer-mode"
              checked={payerMode === 'existing'}
              onChange={() => setPayerMode('existing')}
            />
            {t('customers.addCustomer.payerExisting')}
          </label>
        </div>
      </fieldset>

      {payerMode === 'new' ? (
        <>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('customers.addCustomer.type')}</span>
            <select
              className={field}
              value={type}
              onChange={(event) => setType(event.target.value as 'Residential' | 'Commercial')}
            >
              <option value="Residential">{t('customers.type.Residential')}</option>
              <option value="Commercial">{t('customers.type.Commercial')}</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t(
                type === 'Commercial'
                  ? 'customers.addCustomer.legalName'
                  : 'customers.addCustomer.name',
              )}
            </span>
            <input
              required
              className={field}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        </>
      ) : (
        <div className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.existingPayer')}</span>
          {existingParty ? (
            <p className="flex items-center justify-between rounded-md border border-[var(--color-input)] px-2 py-1.5">
              <span>{existingParty.displayName}</span>
              <button
                type="button"
                className="text-xs underline underline-offset-2"
                onClick={() => setExistingParty(null)}
              >
                {t('customers.addCustomer.changePayer')}
              </button>
            </p>
          ) : (
            <PartyPicker
              onSelect={setExistingParty}
              ariaLabel={t('customers.addCustomer.existingPayer')}
            />
          )}
        </div>
      )}

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.primaryContact')}</span>
        <input
          className={field}
          value={primaryContact}
          onChange={(event) => setPrimaryContact(event.target.value)}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.phone')}</span>
          <input
            type="tel"
            className={field}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.email')}</span>
          <input
            type="email"
            className={field}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.line1')}</span>
        <input
          required
          className={field}
          value={line1}
          onChange={(event) => setLine1(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.line2')}</span>
        <input className={field} value={line2} onChange={(event) => setLine2(event.target.value)} />
      </label>

      <div className="grid grid-cols-4 gap-3">
        <label className="col-span-2 block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.city')}</span>
          <input
            required
            className={field}
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.state')}</span>
          <input
            required
            className={field}
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.postalCode')}</span>
          <input
            required
            className={field}
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customers.addCustomer.notes')}</span>
        <textarea
          rows={2}
          className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {/* items-end, because "How did they hear about us?" wraps to two lines
          in this dialog's width while "Detail (optional)" does not — without
          it the select sits a line lower than the input beside it. Invisible
          to jsdom, which measures every width as zero; found in the browser. */}
      <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customers.addCustomer.leadSource')}</span>
          <select
            className={field}
            value={leadSourceId}
            onChange={(event) => {
              setLeadSourceId(event.target.value);
              if (event.target.value === '') {
                setLeadSourceDetail('');
              }
            }}
          >
            <option value="">{t('customers.addCustomer.leadSourceUnset')}</option>
            {(leadSources.data ?? []).map((source) => (
              <option key={source.id} value={source.id}>
                {source.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t('customers.addCustomer.leadSourceDetail')}
          </span>
          <input
            className={field}
            value={leadSourceDetail}
            // Disabled rather than hidden: the field appearing and vanishing as
            // the picker changes moves everything below it, and the reason it
            // cannot be filled is worth showing rather than hiding.
            disabled={leadSourceId === ''}
            placeholder={t('customers.addCustomer.leadSourceDetailPlaceholder')}
            onChange={(event) => setLeadSourceDetail(event.target.value)}
          />
        </label>
      </div>

      {createCustomer.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('customers.addCustomer.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
